import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useClub } from '../../contexts/ClubContext';

function AdminLayout({ children, title, subtitle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const { club, clubId: contextClubId } = useClub();
  
  // Use URL param as fallback if context hasn't loaded yet
  const clubId = contextClubId || searchParams.get('club');
  const [profile, setProfile] = useState({ firstName: '', lastName: '', displayName: '', role: '' });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setProfile(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };
    fetchProfile();
  }, [user]);

  const displayName = profile.firstName && profile.lastName 
    ? `${profile.firstName} ${profile.lastName}` 
    : profile.displayName || user?.email?.split('@')[0] || 'Admin';

  const isSuperAdmin = profile.role === 'super_admin';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setSaveError('');
    try {
      await setDoc(doc(db, 'users', user.uid), {
        firstName: profile.firstName,
        lastName: profile.lastName,
        displayName: `${profile.firstName} ${profile.lastName}`.trim(),
        updatedAt: new Date()
      }, { merge: true });
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const navItems = [
    { path: '/admin/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/admin/players', icon: '⚽', label: 'Players' },
    { path: '/admin/teams', icon: '👥', label: 'Teams' },
    { path: '/admin/schedule', icon: '📅', label: 'Schedule' },
    { path: '/admin/shop', icon: '🛒', label: 'Kit Shop' },
    { path: '/admin/orders', icon: '📦', label: 'Orders' },
    { path: '/admin/registrations', icon: '📋', label: 'Registrations' },
    { path: '/admin/coaches', icon: '🏃', label: 'Coaches' },
    { path: '/admin/gallery', icon: '📸', label: 'Gallery' },
    { path: '/admin/content', icon: '📝', label: 'Content' },
    { path: '/admin/subscription', icon: '💳', label: 'Subscription', dividerBefore: true },
  ];

  return (
    <div className="admin-dashboard">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">⚽</span>
          <span className="admin-title">{club?.name?.toUpperCase() || 'ADMIN'} - ADMIN</span>
        </div>
        <div className="admin-header-right">
          <button 
            onClick={() => setShowProfileModal(true)} 
            className="admin-profile-btn"
            type="button"
          >
            {displayName}
          </button>
          {isSuperAdmin && (
            <button onClick={() => navigate('/super-admin')} className="admin-super-btn">
              Super Admin
            </button>
          )}
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.dividerBefore && <div className="nav-divider"></div>}
                <a
                  href={`${item.path}?club=${clubId}`}
                  className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </a>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          {(title || subtitle) && (
            <div className="admin-page-header">
              <div>
                {title && <h1>{title}</h1>}
                {subtitle && <p>{subtitle}</p>}
              </div>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {saveError && <div className="form-error">{saveError}</div>}
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={profile.firstName || ''}
                  onChange={e => setProfile({...profile, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={profile.lastName || ''}
                  onChange={e => setProfile({...profile, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="disabled"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowProfileModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLayout;
